import React, { useState } from 'react';
import { useBrand } from '../hooks/useBrand.js';

interface ScoreEntrySimpleProps {
  team1Name: string;
  team2Name: string;
  team1Score: number;
  team2Score: number;
  /** Called when the user confirms the score delta */
  onSubmit: (team1Delta: number, team2Delta: number) => void;
  /** Called when the overlay is dismissed without submitting */
  onDismiss: () => void;
  /** Optional gear button callback — renders gear icon to the left of Cancel when provided */
  onGearTap?: () => void;
  /** Label for the submit button. Defaults to "Submit Round". Pass "Update Round" in correction mode. */
  submitLabel?: string;
}

/**
 * Score entry overlay — Simple mode.
 * +/- buttons for each team's net points this round.
 * Dismissed via Cancel or Submit only.
 *
 * Layout scaling: card is set to 80dvh × 90vw with
 * container-type: size. All internal sizing uses cqh/cqw units relative to
 * the card container so the UI scales proportionally on any tablet size —
 * optimised for 10" landscape tablets. Matches the scaling approach used in
 * Scoreboard.tsx and ScoreEntryIntermediate.tsx.
 *
 * Approximate vertical budget (80dvh = 100cqh of this container):
 *   Padding top+bottom:  4cqh
 *   Title:               7cqh  (font 5cqh + gap 2cqh)
 *   Team columns:        flex:1 (fills remaining)
 *   Action buttons:      ~14cqh (padding 2.5cqh each side + font)
 *   Gaps:                ~4cqh
 */
export function ScoreEntrySimple({
  team1Name,
  team2Name,
  team1Score,
  team2Score,
  onSubmit,
  onDismiss,
  onGearTap,
  submitLabel = 'Submit Round',
}: ScoreEntrySimpleProps) {
  const brand = useBrand();
  const [t1Delta, setT1Delta] = useState(0);
  const [t2Delta, setT2Delta] = useState(0);
  const adjust = (setter: React.Dispatch<React.SetStateAction<number>>, delta: number) => {
    setter((v) => Math.max(0, v + delta));
  };

  const handleSubmit = () => {
    onSubmit(t1Delta, t2Delta);
  };

  const overlayStyle: React.CSSProperties = {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 100,
  };

  /**
   * Card is the CSS size-query container.
   * Height: 80dvh — fills most of the screen vertically.
   * Width: 90vw — fills 90% of viewport; no pixel cap so landscape tablets use full space.
   * All cqh/cqw values below are relative to THIS element's dimensions.
   */
  const cardStyle: React.CSSProperties = {
    backgroundColor: brand.colors.background,
    borderRadius: '1.5cqh',
    height: '80dvh',
    width: '90vw',
    padding: '2cqh 2cqw',
    display: 'flex',
    flexDirection: 'column',
    gap: '2cqh',
    boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
    color: brand.colors.text,
    fontFamily: 'system-ui, sans-serif',
    // Makes this element a CSS container for cqh/cqw queries
    containerType: 'size',
    overflow: 'hidden',
  };

  return (
    <div style={overlayStyle} role="dialog" aria-modal="true" aria-label="Enter round score">
      <div style={cardStyle}>
        {/* Title — 5cqh font ≈ 5% of card height */}
        <h2
          style={{
            margin: 0,
            textAlign: 'center',
            fontSize: '5cqh',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          Enter Score
        </h2>

        {/* Team columns — flex:1 fills remaining height */}
        <div style={{ display: 'flex', gap: '2cqw', flex: 1, minHeight: 0 }}>
          <TeamScoreInput
            name={team1Name}
            currentTotal={team1Score}
            delta={t1Delta}
            color={brand.colors.scoreboard.left}
            textColor={brand.colors.text}
            onIncrement={() => adjust(setT1Delta, 1)}
            onDecrement={() => adjust(setT1Delta, -1)}
          />
          <TeamScoreInput
            name={team2Name}
            currentTotal={team2Score}
            delta={t2Delta}
            color={brand.colors.scoreboard.right}
            textColor={brand.colors.text}
            onIncrement={() => adjust(setT2Delta, 1)}
            onDecrement={() => adjust(setT2Delta, -1)}
          />
        </div>

        {/* Action buttons — fixed height via cqh padding */}
        <div style={{ display: 'flex', gap: '1.5cqw', flexShrink: 0 }}>
          {onGearTap && (
            <button
              onClick={onGearTap}
              aria-label="Game settings"
              style={{
                flexShrink: 0,
                width: '8cqh',
                height: '8cqh',
                padding: 0,
                fontSize: '3.5cqh',
                border: `2px solid ${brand.colors.text}`,
                borderRadius: '1cqh',
                background: 'transparent',
                color: brand.colors.text,
                cursor: 'pointer',
                touchAction: 'manipulation',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              ⚙️
            </button>
          )}
          <button
            onClick={onDismiss}
            style={{
              flex: 1,
              padding: '2.5cqh 0',
              fontSize: '2.5cqh',
              border: `2px solid ${brand.colors.text}`,
              borderRadius: '1cqh',
              background: 'transparent',
              color: brand.colors.text,
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            style={{
              flex: 2,
              padding: '2.5cqh 0',
              fontSize: '3cqh',
              fontWeight: 700,
              border: 'none',
              borderRadius: '1cqh',
              background: brand.colors.primary,
              color: '#ffffff',
              cursor: 'pointer',
              touchAction: 'manipulation',
            }}
          >
            {submitLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// TeamScoreInput — internal
// ============================================================

interface TeamScoreInputProps {
  name: string;
  currentTotal: number;
  delta: number;
  color: string;
  textColor: string;
  onIncrement: () => void;
  onDecrement: () => void;
}

/**
 * Per-team score column inside the Simple overlay.
 * Sizing uses cqh/cqw from the card container (set on parent card div).
 * On a 10" landscape tablet (~640px card height, ~900px wide):
 *   10cqh ≈ 64px buttons (good touch target)
 *   14cqh ≈ 90px score display
 */
function TeamScoreInput({
  name,
  currentTotal,
  delta,
  color,
  textColor,
  onIncrement,
  onDecrement,
}: TeamScoreInputProps) {
  const btnStyle: React.CSSProperties = {
    width: '10cqh',
    height: '10cqh',
    fontSize: '5cqh',
    fontWeight: 700,
    border: 'none',
    borderRadius: '1cqh',
    background: color,
    color: textColor,
    cursor: 'pointer',
    touchAction: 'manipulation',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  return (
    <div
      style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1.5cqh',
      }}
    >
      {/* Team name */}
      <div
        style={{
          fontSize: '3.5cqh',
          fontWeight: 600,
          textAlign: 'center',
          padding: '0.8cqh 1.5cqw',
          borderRadius: '0.8cqh',
          background: color,
          color: textColor,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {name}
      </div>

      {/* Running total */}
      <div style={{ fontSize: '14cqh', fontWeight: 800, lineHeight: 1 }}>
        {currentTotal + delta}
      </div>

      {/* Round delta label */}
      <div style={{ fontSize: '2.5cqh', opacity: 0.6 }}>
        {delta >= 0 ? '+' : ''}{delta} this round
      </div>

      {/* +/− buttons */}
      <div style={{ display: 'flex', gap: '2cqw' }}>
        <button onClick={onDecrement} style={btnStyle} aria-label={`Decrease ${name} score`}>
          −
        </button>
        <button onClick={onIncrement} style={btnStyle} aria-label={`Increase ${name} score`}>
          +
        </button>
      </div>
    </div>
  );
}
